import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../common/CommonSelect";
import { format, parse, isValid } from "date-fns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

// ============================================
// Constants and Mappings
// ============================================

const stateMapping = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const fullStates = Object.values(stateMapping).sort((a, b) =>
  a.localeCompare(b),
);

// ============================================
// Helper Functions
// ============================================

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;

  // Try yyyy-MM-dd
  let parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return parsed;

  // Try MM/dd/yyyy
  parsed = parse(dateStr, "MM/dd/yyyy", new Date());
  if (isValid(parsed)) return parsed;

  // Try new Date
  const fallback = new Date(dateStr);
  if (isValid(fallback)) return fallback;

  return null;
};

const getMappedGender = (gender) => {
  if (!gender) return "";
  const g = String(gender).trim().toUpperCase();
  switch (g) {
    case "M":
    case "1":
    case "MALE":
      return "MALE";
    case "F":
    case "2":
    case "FEMALE":
      return "FEMALE";
    default:
      return "";
  }
};

const getDisplayValue = (value, disabled, capitalize = false) => {
  if (disabled) {
    if (!value) return "-";
    return capitalize ? capitalizeFirstLetter(value) : value;
  }
  return value || "";
};

const getPhoneDisplayValue = (value, disabled) => {
  if (disabled && !value) return "-";
  return value || "";
};

// ============================================
// Styles
// ============================================

const textFieldSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
    borderRadius: "8px",
    height: "43px",
    "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: disabled ? "#999893" : "#0e1324",
    pr: 0,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#999893",
    fontSize: "14px",
  },
});

const labelSx = {
  fontSize: "13px",
  color: "#66638C",
  fontWeight: 500,
  mb: "8px",
  display: "block",
};

// ============================================
// Reusable TextField with Character Counter
// ============================================

const CharacterLimitedTextField = ({
  label,
  placeholder,
  value,
  onChange,
  disabled,
  maxLength,
  error,
  helperText,
  sx,
  capitalize = false,
  phone = false,
  ...props
}) => {
  const displayValue = phone
    ? getPhoneDisplayValue(value, disabled)
    : getDisplayValue(value, disabled, capitalize);

  const handlePhoneChange = (e) => {
    const sanitized = e.target.value.replaceAll(/\D/g, "").slice(0, maxLength);
    onChange(sanitized);
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  const handleChange = phone ? handlePhoneChange : handleTextChange;

  const showCounter = !disabled && maxLength > 0;
  const currentLength = (value || "").length;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      {label && (
        <Typography sx={labelSx}>
          {label}
          {props.required && <span style={{ color: "#d32f2f" }}>*</span>}
        </Typography>
      )}
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={displayValue}
        disabled={disabled}
        error={!!error}
        helperText={helperText}
        onChange={handleChange}
        slotProps={
          showCounter
            ? {
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {currentLength}/{maxLength}
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }
            : undefined
        }
        sx={sx}
        {...props}
      />
    </Box>
  );
};

CharacterLimitedTextField.propTypes = {
  label: PropTypes.node,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  error: PropTypes.any,
  helperText: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  capitalize: PropTypes.bool,
  phone: PropTypes.bool,
  required: PropTypes.bool,
};

// ============================================
// Reusable Date Picker
// ============================================

const DatePickerField = ({ value, onChange, disabled, error, helperText }) => {
  if (disabled && !value) {
    return (
      <TextField
        fullWidth
        size="small"
        value="-"
        disabled
        sx={textFieldSx(disabled)}
      />
    );
  }

  return (
    <DesktopDatePicker
      disabled={disabled}
      value={parseDateString(value)}
      onChange={(newValue) => {
        const formattedDate =
          newValue && isValid(newValue) ? format(newValue, "yyyy-MM-dd") : "";
        onChange(formattedDate);
      }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          placeholder: "MM/DD/YYYY",
          error: !!error,
          helperText: helperText,
          sx: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
              borderRadius: "8px",
              height: "43px",
              "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              color: disabled ? "#999893" : "#0e1324",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#999893",
              fontSize: "14px",
            },
          },
        },
      }}
    />
  );
};

DatePickerField.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.any,
  helperText: PropTypes.string,
};

// ============================================
// Reusable Gender Select
// ============================================

const GenderSelect = ({ value, onChange, disabled, error }) => {
  if (disabled && !value) {
    return (
      <TextField
        fullWidth
        size="small"
        value="-"
        disabled
        sx={textFieldSx(disabled)}
      />
    );
  }

  const mappedValue = getMappedGender(value);

  return (
    <FormControl fullWidth size="small" error={!!error}>
      <Select
        value={mappedValue}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": {
            border: disabled ? "none" : "1px solid #E8E8E9",
          },
          height: "43px",
          "& .MuiSelect-select.Mui-disabled": {
            WebkitTextFillColor: "#999893",
          },
        }}
      >
        <MenuItem value="MALE">Male</MenuItem>
        <MenuItem value="FEMALE">Female</MenuItem>
      </Select>
      {error && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: "3px", ml: "14px" }}
        >
          {error}
        </Typography>
      )}
    </FormControl>
  );
};

GenderSelect.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.any,
};

// ============================================
// Reusable State Select
// ============================================

const StateSelect = ({ value, onChange, disabled, error, anchorRef }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (type) => {
    const code = Object.keys(stateMapping).find(
      (key) => stateMapping[key] === type,
    );
    onChange(code || type);
    handleClose();
  };

  const displayValue =
    stateMapping[value] || value || (disabled ? "-" : "Select State");

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          width: "100%",
          height: "43px",
          border: disabled ? "none" : "1px solid",
          borderColor: error ? "#d32f2f" : "#E8E8E9",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 12px",
          backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
          cursor: disabled ? "default" : "pointer",
          "&:hover": {
            backgroundColor: disabled ? "#F2F2F2" : "#F9FAFB",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: !disabled && value ? "#0e1324" : "#999893",
          }}
        >
          {displayValue}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            color: disabled ? "#C4C2D3" : "#6B7280",
            fontSize: "20px",
            transition: "transform 0.2s",
            transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      {error && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: "3px", ml: "14px" }}
        >
          {error}
        </Typography>
      )}
      <CommonSelect
        id="patient-state-select"
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        noteTypes={fullStates}
        selectedTypes={[stateMapping[value] || value]}
        handleTypeToggle={handleSelect}
        width={
          anchorRef.current?.offsetWidth
            ? `${anchorRef.current.offsetWidth}px`
            : "240px"
        }
      />
    </>
  );
};

StateSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.any,
  anchorRef: PropTypes.object,
};

// ============================================
// Main Component
// ============================================

const PatientDemographics = ({ data, onChange, disabled, errors = {} }) => {
  const stateAnchorRef = useRef(null);

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
        >
          Patient Demographics
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{ color: "#7F7C9F", fontSize: "14px", fontWeight: 400 }}
          ></Typography>
          <Typography
            sx={{
              color: data.phoneSupportsSms ? "#45B184" : "#CC0000",
              fontSize: "12px",
              fontWeight: 700,
            }}
          >
            {data.phoneSupportsSms
              ? "(Text consent obtained)"
              : "(Text consent missing)"}
          </Typography>
        </Box>
      </Box>

      {/* Name Row */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <CharacterLimitedTextField
          label="First Name"
          placeholder="Enter first name"
          value={data.firstName}
          onChange={(val) => onChange("firstName", val)}
          disabled={disabled}
          maxLength={32}
          error={errors.firstName}
          helperText={errors.firstName}
          sx={textFieldSx(disabled)}
          capitalize={!disabled}
          required
        />
        <CharacterLimitedTextField
          label="Last Name"
          placeholder="Enter last name"
          value={data.lastName}
          onChange={(val) => onChange("lastName", val)}
          disabled={disabled}
          maxLength={32}
          error={errors.lastName}
          helperText={errors.lastName}
          sx={textFieldSx(disabled)}
          capitalize={!disabled}
          required
        />
      </Box>

      {/* Date of Birth & Gender Row */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            Date of Birth <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <DatePickerField
            value={data.dateOfBirth}
            onChange={(val) => onChange("dateOfBirth", val)}
            disabled={disabled}
            error={errors.dateOfBirth}
            helperText={errors.dateOfBirth}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>Gender</Typography>
          <GenderSelect
            value={data.gender}
            onChange={(val) => onChange("gender", val)}
            disabled={disabled}
            error={errors.gender}
          />
        </Box>
      </Box>

      {/* Address Row */}
      <CharacterLimitedTextField
        label="Street"
        placeholder="Enter street address"
        value={data.addressLine1}
        onChange={(val) => onChange("addressLine1", val)}
        disabled={disabled}
        maxLength={60}
        error={errors.addressLine1}
        helperText={errors.addressLine1}
        sx={textFieldSx(disabled)}
        capitalize={!disabled}
        required
      />

      <CharacterLimitedTextField
        label="Line 2"
        placeholder="Enter line 2"
        value={data.addressLine2}
        onChange={(val) => onChange("addressLine2", val)}
        disabled={disabled}
        maxLength={60}
        error={errors.addressLine2}
        helperText={errors.addressLine2}
        sx={textFieldSx(disabled)}
        capitalize={!disabled}
      />

      {/* City, State, Zip Row */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <CharacterLimitedTextField
            label="City"
            placeholder="Enter city"
            value={data.city}
            onChange={(val) => onChange("city", val)}
            disabled={disabled}
            maxLength={50}
            error={errors.city}
            helperText={errors.city}
            sx={textFieldSx(disabled)}
            capitalize={!disabled}
            required
          />
        </Box>
        <Box
          sx={{
            flex: 2,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <Typography sx={labelSx}>
            State <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <StateSelect
            value={data.stateProvince}
            onChange={(val) => onChange("stateProvince", val)}
            disabled={disabled}
            error={errors.stateProvince}
            anchorRef={stateAnchorRef}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          <CharacterLimitedTextField
            label="Zip Code"
            placeholder="Enter zip code"
            value={data.postalCode}
            onChange={(val) => onChange("postalCode", val)}
            disabled={disabled}
            maxLength={10}
            error={errors.postalCode}
            helperText={errors.postalCode}
            sx={textFieldSx(disabled)}
            required
          />
        </Box>
      </Box>

      {/* Phone & Email Row */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <CharacterLimitedTextField
          label="Phone Number"
          placeholder="Enter phone number"
          value={data.phone}
          onChange={(val) => onChange("phone", val)}
          disabled={disabled}
          maxLength={10}
          error={errors.phone}
          helperText={errors.phone}
          sx={textFieldSx(disabled)}
          phone
        />
        <CharacterLimitedTextField
          label="Email Address"
          placeholder="Enter email address"
          value={data.email}
          onChange={(val) => onChange("email", val)}
          disabled={disabled}
          maxLength={74}
          error={errors.email}
          helperText={errors.email}
          sx={textFieldSx(disabled)}
        />
      </Box>
    </Box>
  );
};

PatientDemographics.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

PatientDemographics.defaultProps = {
  disabled: false,
  errors: {},
};

export default PatientDemographics;
